// P9/S7 — minimal safe markdown renderer for the article reader.
//
// customer-web has NO markdown dependency and the port rules forbid adding
// one mid-batch (lockfile single-hand rule) — so the reader renders a small,
// safe-by-construction subset as REACT ELEMENTS (no dangerouslySetInnerHTML,
// no HTML pass-through: raw HTML in the body renders as literal text, which
// composes with the server's sanitize-on-write boundary rather than trusting
// it). Recorded as an S7 follow-up to swap for a real renderer (e.g.
// react-markdown) when the dep can land.
//
// Supported subset: # ## ### headings · ``` code fences · - / * bullets ·
// 1. numbered lists · > blockquote · **bold** · *italic* · `inline code` ·
// [label](https://…) links (http/https only — anything else renders as text).

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Optional second pass over the PROSE runs of a body — the plain text, and the
 * text inside **bold** / *italic*. Code spans, fenced code and link labels are
 * never handed over: what they carry is quoted, not prose.
 *
 * The assistant panel uses it to turn screen names into rail links
 * (features/support-assistant/screen-links). Returning the text unchanged is
 * always valid; the default does exactly that.
 */
export type MarkdownTextDecorator = (text: string, edges: MarkdownRunEdges) => ReactNode;

/**
 * The characters flanking a run in the SOURCE line, empty string at a line
 * edge. A decorator matching whole words needs them: the bold run of
 * `**Görevler**imi` is the bare word "Görevler", and without its right-hand
 * neighbour a decorator would read the suffix as absent and link a fragment.
 */
export interface MarkdownRunEdges {
  readonly before: string;
  readonly after: string;
}

// ── inline spans ────────────────────────────────────────────────────────────

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(
  text: string,
  keyPrefix: string,
  decorate: MarkdownTextDecorator,
): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  const edgesAt = (from: number, to: number): MarkdownRunEdges => ({
    before: text[from - 1] ?? "",
    after: text[to] ?? "",
  });
  for (const m of text.matchAll(INLINE_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) {
      out.push(decorate(text.slice(last, idx), edgesAt(last, idx)));
    }
    const token = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith("**")) {
      const strong = decorate(token.slice(2, -2), edgesAt(idx, idx + token.length));
      out.push(<strong key={key}>{strong}</strong>);
    } else if (token.startsWith("`")) {
      out.push(
        <code key={key} className="rounded bg-[var(--bg-subtle)] px-1 font-mono text-[0.9em]">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("[")) {
      const close = token.indexOf("](");
      const label = token.slice(1, close);
      const href = token.slice(close + 2, -1);
      if (/^https?:\/\//i.test(href)) {
        out.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[var(--accent)] underline"
          >
            {label}
          </a>,
        );
      } else {
        // Non-http(s) scheme (javascript:, data:, …) — render as plain text.
        out.push(token);
      }
    } else {
      const em = decorate(token.slice(1, -1), edgesAt(idx, idx + token.length));
      out.push(<em key={key}>{em}</em>);
    }
    last = idx + token.length;
  }
  if (last < text.length) {
    out.push(decorate(text.slice(last), edgesAt(last, text.length)));
  }
  return out;
}

// ── block parser ────────────────────────────────────────────────────────────

/** Render a markdown string as a safe React element tree. */
export function MarkdownBody({
  markdown,
  decorateText = (text): ReactNode => text,
  className,
}: {
  readonly markdown: string;
  readonly decorateText?: MarkdownTextDecorator;
  /** Body typography for surfaces that are not the article reader. */
  readonly className?: string;
}): React.ReactElement {
  const lines = markdown.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    if (line.trim() === "") {
      i++;
      continue;
    }

    // Code fence — verbatim until the closing fence.
    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) {
        buf.push(lines[i] ?? "");
        i++;
      }
      i++; // skip the closing fence
      blocks.push(
        <pre
          key={key++}
          className="overflow-x-auto rounded-lg bg-[var(--bg-subtle)] p-3 font-mono text-[12.5px] leading-relaxed"
        >
          {buf.join("\n")}
        </pre>,
      );
      continue;
    }

    // Headings.
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading !== null) {
      const level = (heading[1] ?? "#").length;
      const content = renderInline(heading[2] ?? "", `h${key}`, decorateText);
      blocks.push(
        level === 1 ? (
          <h2 key={key++} className="font-serif text-[19px] font-medium text-[var(--fg-primary)]">
            {content}
          </h2>
        ) : level === 2 ? (
          <h3 key={key++} className="font-serif text-[16px] font-medium text-[var(--fg-primary)]">
            {content}
          </h3>
        ) : (
          <h4 key={key++} className="text-[14px] font-semibold text-[var(--fg-primary)]">
            {content}
          </h4>
        ),
      );
      i++;
      continue;
    }

    // Bullet / numbered lists.
    const isBullet = (l: string): boolean => /^\s*[-*]\s+/.test(l);
    const isNumbered = (l: string): boolean => /^\s*\d+\.\s+/.test(l);
    if (isBullet(line) || isNumbered(line)) {
      const numbered = isNumbered(line);
      const items: ReactNode[] = [];
      while (
        i < lines.length &&
        (numbered ? isNumbered(lines[i] ?? "") : isBullet(lines[i] ?? ""))
      ) {
        const item = (lines[i] ?? "").replace(numbered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/, "");
        const itemContent = renderInline(item, `li${key}-${items.length}`, decorateText);
        items.push(<li key={items.length}>{itemContent}</li>);
        i++;
      }
      blocks.push(
        numbered ? (
          <ol key={key++} className="list-decimal pl-5">
            {items}
          </ol>
        ) : (
          <ul key={key++} className="list-disc pl-5">
            {items}
          </ul>
        ),
      );
      continue;
    }

    // Blockquote.
    if (line.startsWith(">")) {
      const buf: string[] = [];
      while (i < lines.length && (lines[i] ?? "").startsWith(">")) {
        buf.push((lines[i] ?? "").replace(/^>\s?/, ""));
        i++;
      }
      const quoteContent = renderInline(buf.join(" "), `bq${key}`, decorateText);
      blocks.push(
        <blockquote
          key={key++}
          className="border-l-2 border-[var(--accent)] pl-3 italic text-[var(--fg-secondary)]"
        >
          {quoteContent}
        </blockquote>,
      );
      continue;
    }

    // Paragraph — consecutive non-empty, non-special lines join.
    const buf: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() !== "" &&
      !(lines[i] ?? "").startsWith("```") &&
      !/^(#{1,3})\s+/.test(lines[i] ?? "") &&
      !/^\s*[-*]\s+/.test(lines[i] ?? "") &&
      !/^\s*\d+\.\s+/.test(lines[i] ?? "") &&
      !(lines[i] ?? "").startsWith(">")
    ) {
      buf.push(lines[i] ?? "");
      i++;
    }
    const paragraphContent = renderInline(buf.join(" "), `p${key}`, decorateText);
    blocks.push(<p key={key++}>{paragraphContent}</p>);
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 font-serif text-[14.5px] leading-relaxed text-[var(--fg-primary)]",
        className,
      )}
    >
      {blocks}
    </div>
  );
}
